import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

import avatarFallback from "@/assets/UI/avatar.png";
import coinIcon from "@/assets/UI/coin.webp";
import walletIcon from "@/assets/UI/wallet.webp";

import { formatDateTime } from "@/utils/formatDateTime";
import s from "./MembershipRevenueEntryDetail.module.scss";

const getUserId = (u) => u?.id || u?._id || u?.userId || null;

const formatAmount = (value) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return "0";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const MembershipRevenueEntryDetail = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();

  const [entry, setEntry] = useState(null);
  const [account, setAccount] = useState(null);
  const [user, setUser] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [walletId, setWalletId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchEntry = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(
          `/v1/admin/finance/accounts/membership-revenue/transactions/${entryId}`,
        );

        if (!isMounted) return;

        setEntry(res.data?.entry ?? null);
        setAccount(res.data?.account ?? null);
        setUser(res.data?.user ?? null);
        setAvatar(res.data?.avatar ?? null);
        setWalletId(res.data?.walletId ?? null);
      } catch (err) {
        if (!isMounted) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load membership revenue entry",
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchEntry();

    return () => {
      isMounted = false;
    };
  }, [entryId]);

  const isCredit = entry?.entryType === "credit";
  const userId = useMemo(() => getUserId(user), [user]);

  const walletPath = useMemo(() => {
    return walletId ? `/admin/finance/wallets/${walletId}` : null;
  }, [walletId]);

  const renderPrettyDateTime = (value) => {
    const dt = formatDateTime(value);
    if (!dt) return <span className={s.dash}>-</span>;

    const hasParts =
      dt.day !== undefined && dt.suffix && dt.month && dt.year !== undefined;

    return (
      <div className={s.dateTime}>
        <div className={s.date}>
          {hasParts ? (
            <>
              {dt.day}
              <sup className={s.sup}>{dt.suffix}</sup> {dt.month} {dt.year}
            </>
          ) : (
            dt.date
          )}
        </div>
        <div className={s.time}>{dt.time}</div>
      </div>
    );
  };

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerLeft}>
          <p className={s.eyebrow}>Finance • Membership revenue</p>
          <h1 className={s.title}>Membership Revenue Entry</h1>
          <p className={s.subtitle}>
            Review the member, revenue amount, and timing for this transaction.
          </p>
        </div>

        <div className={s.actions}>
          <button className={s.btn} onClick={() => navigate(-1)}>
            Back
          </button>

          <Link
            className={`${s.btn} ${s.btnLedger}`}
            to="/admin/finance/accounts/membership-revenue"
          >
            <span className={s.emoji}>💎</span>
            <span>Revenue Ledger</span>
          </Link>

          {walletPath ? (
            <Link className={`${s.btn} ${s.btnWallet}`} to={walletPath}>
              <img className={s.btnIcon} src={walletIcon} alt="" />
              <span>Wallet</span>
            </Link>
          ) : (
            <span
              className={`${s.btn} ${s.btnDisabled}`}
              title="Wallet not available"
            >
              <img className={s.btnIcon} src={walletIcon} alt="" />
              <span>Wallet</span>
            </span>
          )}
        </div>
      </header>

      {loading ? (
        <div className={s.stateBox}>Loading membership revenue entry…</div>
      ) : error ? (
        <div className={`${s.stateBox} ${s.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      ) : !entry ? (
        <div className={s.stateBox}>Transaction not found.</div>
      ) : (
        <div className={s.grid}>
          {/* Member */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Member</h2>
              {walletPath ? (
                <Link className={s.microLink} to={walletPath}>
                  Open wallet →
                </Link>
              ) : (
                <span className={s.hint}>No wallet</span>
              )}
            </div>

            {walletPath ? (
              <Link to={walletPath} className={s.memberRow}>
                <span className={s.avatarRing}>
                  <img
                    src={avatar?.url || avatarFallback}
                    alt=""
                    className={s.avatarLg}
                  />
                </span>

                <span className={s.memberMeta}>
                  <span className={s.memberName}>
                    {user?.username || "Unknown user"}
                  </span>
                  <span className={s.memberSub}>Tap to view the wallet</span>
                </span>
              </Link>
            ) : (
              <div className={s.memberRowStatic}>
                <span className={s.avatarRing}>
                  <img
                    src={avatar?.url || avatarFallback}
                    alt=""
                    className={s.avatarLg}
                  />
                </span>

                <div className={s.memberMeta}>
                  <div className={s.memberName}>
                    {user?.username || "Unknown user"}
                  </div>
                  <div className={s.memberSub}>Wallet not available</div>
                </div>
              </div>
            )}

            <div className={s.divider} />

            <div className={s.balanceBox}>
              <div className={s.balanceTop}>
                <div>
                  <p className={s.muted}>Current revenue balance</p>
                  <p className={s.balanceName}>
                    {account?.name || "Membership"}
                  </p>
                </div>

                <span
                  className={`${s.typePill} ${
                    isCredit ? s.typeCredit : s.typeDebit
                  }`}
                >
                  {entry.entryType || "-"}
                </span>
              </div>

              <div className={s.balanceMetrics}>
                <div className={s.metricCard}>
                  <p className={s.muted}>NOTY</p>
                  <div className={s.metricLine}>
                    <span className={s.metricValue}>
                      {formatAmount(account?.balanceNoty ?? 0)}
                    </span>
                    <img className={s.coinMd} src={coinIcon} alt="" />
                  </div>
                </div>

                <div className={s.metricCard}>
                  <p className={s.muted}>CAD</p>
                  <div className={s.metricLine}>
                    <span className={s.metricValue}>
                      {formatAmount(account?.balanceCad ?? 0)}
                    </span>
                    <span className={s.metricUnit}>CAD</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Transaction */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Transaction</h2>
              <span className={s.hint}>Reason + amount</span>
            </div>

            <div className={s.kvGrid}>
              <div className={s.k}>
                <p className={s.muted}>Reason</p>
                <p className={s.bigText}>{entry.reason || "-"}</p>
              </div>
              <div className={s.k}>
                <p className={s.muted}>Amount</p>
                <div
                  className={`${s.amountLine} ${
                    isCredit ? s.amountCredit : s.amountDebit
                  }`}
                >
                  <span className={s.sign}>{isCredit ? "+" : "-"}</span>
                  <span className={s.amountValue}>
                    {formatAmount(entry.amountNoty ?? 0)}
                  </span>
                  <img className={s.coinSm} src={coinIcon} alt="" />
                </div>
                <p className={s.helperText}>
                  CAD {formatAmount(entry.amountCad ?? 0)}
                </p>
              </div>
            </div>
            <div className={s.dividerSoft} />
            <div className={s.kvGrid}>
              <div className={s.k}>
                <p className={s.muted}>Exchange rate</p>
                <p className={s.valueText}>
                  {formatAmount(entry.exchangeRate)}
                </p>
              </div>
              <div className={s.k}>
                <p className={s.muted}>Balance after</p>
                <div className={s.balanceAfterLine}>
                  <span className={s.balanceAfterValue}>
                    {formatAmount(entry.balanceAfterNoty ?? 0)}
                  </span>
                  <img className={s.coinSm} src={coinIcon} alt="" />
                </div>
                <p className={s.helperText}>
                  CAD {formatAmount(entry.balanceAfterCad ?? 0)}
                </p>
              </div>
            </div>
          </section>

          {/* Timing */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>Timing</h2>
              <span className={s.hint}>Created + updated</span>
            </div>

            <div className={s.kvGrid}>
              <div className={s.k}>
                <p className={s.muted}>Created</p>
                {renderPrettyDateTime(entry.createdAt)}
              </div>

              <div className={s.k}>
                <p className={s.muted}>Last updated</p>
                {renderPrettyDateTime(entry.updatedAt)}
              </div>
            </div>
          </section>

          {/* References */}
          <section className={s.card}>
            <div className={s.cardHead}>
              <h2 className={s.sectionTitle}>References</h2>
              <span className={s.hint}>Source + tracking</span>
            </div>

            <div className={s.kv}>
              <div className={s.k}>
                <p className={s.muted}>Source type</p>
                <p className={s.valueText}>{entry.sourceType || "-"}</p>
              </div>

              <div className={s.k}>
                <p className={s.muted}>Source ID</p>
                <p className={s.valueText}>{entry.sourceId || "-"}</p>
              </div>

              <div className={s.k}>
                <p className={s.muted}>IP address</p>
                <p className={s.valueText}>{entry.ipAddress || "-"}</p>
              </div>

              <div className={s.k}>
                <p className={s.muted}>Member user</p>
                <p className={s.valueText}>{userId || "-"}</p>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default MembershipRevenueEntryDetail;
